import React, { useEffect, useState } from "react";
import { Search, MapPin, Globe } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

const SEO_FIELDS = [
  { k: "title", l: "Meta title", type: "text" },
  { k: "description", l: "Meta description", type: "textarea" },
  { k: "keywords", l: "Keywords (comma separated)", type: "text" },
  { k: "og_image", l: "Social share image URL", type: "text" },
  { k: "twitter_handle", l: "Twitter/X handle", type: "text" },
];
const GEO_FIELDS = [
  { k: "business_name", l: "Business name" },
  { k: "street", l: "Street address" },
  { k: "city", l: "City" }, { k: "region", l: "Province/Region" },
  { k: "postal_code", l: "Postal code" }, { k: "country", l: "Country code" },
  { k: "latitude", l: "Latitude" }, { k: "longitude", l: "Longitude" },
  { k: "phone", l: "Phone" }, { k: "email", l: "Email" },
  { k: "service_areas", l: "Service areas (comma separated)" },
];

export default function AdminSeo() {
  const [seo, setSeo] = useState(null);
  const [geo, setGeo] = useState(null);

  useEffect(() => {
    api.get("/admin/settings/seo").then((r) => setSeo(r.data || {})).catch(() => setSeo({}));
    api.get("/admin/settings/geo").then((r) => setGeo(r.data || {})).catch(() => setGeo({}));
  }, []);

  const saveSeo = async () => {
    try { await api.put("/admin/settings/seo", { value: seo }); toast.success("SEO saved — live now"); }
    catch { toast.error("Could not save SEO. Try again."); }
  };
  const saveGeo = async () => {
    try { await api.put("/admin/settings/geo", { value: geo }); toast.success("GEO saved — live now"); }
    catch { toast.error("Could not save GEO. Try again."); }
  };

  if (!seo || !geo) return <p className="text-muted-foreground">Loading…</p>;

  return (
    <div data-testid="admin-seo">
      <h1 className="font-display text-3xl md:text-4xl font-medium text-primary mb-1">SEO & GEO Tools</h1>
      <p className="text-muted-foreground text-sm mb-8">Search engine and local/geo metadata. Changes publish instantly to the live site — no redeployment.</p>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* SEO */}
        <div className="bg-card border border-primary/10 rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-4"><Search size={18} className="text-secondary" /><h2 className="font-display text-xl font-semibold text-primary">Search Engine (SEO)</h2></div>
          <div className="space-y-4">
            {SEO_FIELDS.map((f) => (
              <div key={f.k}>
                <label className="text-xs text-muted-foreground">{f.l}</label>
                {f.type === "textarea" ? (
                  <textarea rows={3} data-testid={`seo-${f.k}`} value={seo[f.k] || ""} onChange={(e) => setSeo({ ...seo, [f.k]: e.target.value })} className="w-full mt-1 rounded-xl border border-input bg-background px-3 py-2 text-sm" />
                ) : (
                  <input data-testid={`seo-${f.k}`} value={seo[f.k] || ""} onChange={(e) => setSeo({ ...seo, [f.k]: e.target.value })} className="w-full mt-1 rounded-xl border border-input bg-background px-3 py-2 text-sm" />
                )}
              </div>
            ))}
          </div>
          {/* Google preview */}
          <div className="mt-5 p-4 rounded-xl bg-background border border-primary/10">
            <p className="text-xs text-muted-foreground mb-2 flex items-center gap-1"><Globe size={12} /> Search preview</p>
            <p className="text-[#1a0dab] text-lg leading-tight truncate">{seo.title || "Page title"}</p>
            <p className="text-[#006621] text-xs">gybclub.ca</p>
            <p className="text-sm text-[#545454] line-clamp-2">{seo.description || "Meta description preview…"}</p>
          </div>
          <button onClick={saveSeo} data-testid="seo-save" className="mt-5 rounded-full bg-secondary text-secondary-foreground px-6 py-2.5 text-sm font-semibold hover:opacity-90">Save SEO</button>
        </div>

        {/* GEO */}
        <div className="bg-card border border-primary/10 rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-4"><MapPin size={18} className="text-secondary" /><h2 className="font-display text-xl font-semibold text-primary">Local / GEO</h2></div>
          <div className="grid sm:grid-cols-2 gap-4">
            {GEO_FIELDS.map((f) => (
              <div key={f.k} className={["business_name", "street", "service_areas"].includes(f.k) ? "sm:col-span-2" : ""}>
                <label className="text-xs text-muted-foreground">{f.l}</label>
                <input data-testid={`geo-${f.k}`} value={geo[f.k] || ""} onChange={(e) => setGeo({ ...geo, [f.k]: e.target.value })} className="w-full mt-1 rounded-xl border border-input bg-background px-3 py-2 text-sm" />
              </div>
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-4">Powers the LocalBusiness structured data (JSON-LD) injected into every page for local search & maps.</p>
          <button onClick={saveGeo} data-testid="geo-save" className="mt-5 rounded-full bg-secondary text-secondary-foreground px-6 py-2.5 text-sm font-semibold hover:opacity-90">Save GEO</button>
        </div>
      </div>
    </div>
  );
}
